import { EntityType, } from "@withorbit/core";
import Dexie from "dexie";
import { DexieDatabase } from "./dexie/dexie.js";
import { DexieDerivedTaskComponentKeys, DexieEntityKeys, DexieEventKeys, DexieTable, } from "./dexie/tables.js";
export class IDBDatabaseBackend {
    db;
    constructor(databaseName = "OrbitDatabase", indexedDB = window.indexedDB) {
        this.db = new DexieDatabase(databaseName, indexedDB);
    }
    async close() {
        // Warning: we have no way to ensure that all writes have resolved before this statement completes
        this.db.close();
    }
    async getEntities(entityIDs) {
        if (entityIDs.length === 0) {
            return new Map();
        }
        const rows = await this.db.entities
            .where(DexieEntityKeys.ID)
            .anyOf(entityIDs)
            .toArray();
        return extractEntityRecordMapFromRows(rows);
    }
    async getEvents(eventIDs) {
        const values = await this.db.events
            .where(DexieEventKeys.ID)
            .anyOf(eventIDs)
            .toArray();
        const output = new Map();
        for (const value of values) {
            if (!value)
                continue;
            const entity = JSON.parse(value.data);
            output.set(value.id, entity);
        }
        return output;
    }
    async listEntities(query) {
        let request;
        if (query.predicate) {
            const includedEntityIDs = new Set();
            // only one possible key right now, when another key is eventually defined convert this
            // to be an if-else
            // We don't need to filter by entityType in this case because dueTimestamp only applies
            // to tasks. Other predicates may require filtering by entityType.
            switch (query.predicate[0]) {
                case DexieDerivedTaskComponentKeys.DueTimestampMillis:
                    const clause = this.db.derived_taskComponents.where(DexieDerivedTaskComponentKeys.DueTimestampMillis);
                    const derivedRowsPrimaryKeys = await compareUsingPredicate(clause, query.predicate).primaryKeys();
                    for (const [derivedPrimaryKey] of derivedRowsPrimaryKeys) {
                        includedEntityIDs.add(derivedPrimaryKey);
                    }
            }
            // was the afterID specified at the same time?
            if (query.afterID) {
                // simulate a join across the two tables
                const afterRowID = await this._fetchPrimaryKeyFromUniqueKey(this.db.entities, DexieEventKeys.ID, query.afterID);
                // Note that our approach here involves fetching _all_ matching entity IDs, then skipping the
                // ones before the requested row. This means pagination over N entities with a predicate has
                // quadratic time cost.
                // rows are already sorted since we are using the primary key index
                const baseQuery = this.db.entities
                    .where(DexieEntityKeys.RowID)
                    .above(afterRowID)
                    .filter((row) => includedEntityIDs.has(row.id));
                request = applyOptionalLimit(baseQuery, query.limit).toArray();
            }
            else {
                const baseQuery = this.db.entities
                    .where(DexieEntityKeys.ID)
                    .anyOf([...includedEntityIDs]);
                request = applyOptionalLimit(baseQuery, query.limit).sortBy(DexieEntityKeys.RowID);
            }
        }
        else {
            const afterRowID = query.afterID
                ? await this._fetchPrimaryKeyFromUniqueKey(this.db.entities, DexieEventKeys.ID, query.afterID)
                : null;
            const baseQuery = this.db.entities
                .where(`[${DexieEntityKeys.EntityType}+${DexieEntityKeys.RowID}]`)
                .between([query.entityType, afterRowID === null ? 0 : afterRowID + 1], [query.entityType, Dexie.Dexie.maxKey]);
            // We don't need to explicitly sort the result, because rowID is the final column of this compound index.
            request = applyOptionalLimit(baseQuery, query.limit).toArray();
        }
        const queriedEntities = await request;
        return queriedEntities.map((entity) => ({
            lastEventID: entity.lastEventID,
            lastEventTimestampMillis: entity.lastEventTimestampMillis,
            entity: JSON.parse(entity.data),
        }));
    }
    async listEvents(query) {
        let request;
        if (query.predicate && query.afterID) {
            const afterSequenceNumber = await this._fetchPrimaryKeyFromUniqueKey(this.db.events, DexieEventKeys.ID, query.afterID);
            // both the predicate and afterID were specified need to do more complex querying
            if (query.predicate[1] == "=") {
                // can create a compound query in this case. It is already sorted since the primary key is the final
                // column of the index
                const baseQuery = this.db.events
                    .where(`[${query.predicate[0]}+${DexieEventKeys.SequenceNumber}]`)
                    .between([query.predicate[2], afterSequenceNumber + 1], [query.predicate[2], Dexie.Dexie.maxKey]);
                request = applyOptionalLimit(baseQuery, query.limit).toArray();
            }
            else {
                // arbitrary comparison query
                const predicatedQuery = this.db.events.where(query.predicate[0]);
                const baseQuery = compareUsingPredicate(predicatedQuery, query.predicate).filter((item) => item[DexieEventKeys.SequenceNumber] > afterSequenceNumber);
                request = applyOptionalLimit(baseQuery, query.limit).sortBy(DexieEventKeys.SequenceNumber);
            }
        }
        else if (query.predicate) {
            const predicatedQuery = this.db.events.where(query.predicate[0]);
            request = applyOptionalLimit(compareUsingPredicate(predicatedQuery, query.predicate), query.limit).sortBy(DexieEventKeys.SequenceNumber);
        }
        else if (query.afterID) {
            const afterSequenceNumber = await this._fetchPrimaryKeyFromUniqueKey(this.db.events, DexieEventKeys.ID, query.afterID);
            // already sorted since fetching using primary key;
            const baseQuery = this.db.events
                .where(DexieEventKeys.SequenceNumber)
                .above(afterSequenceNumber);
            request = applyOptionalLimit(baseQuery, query.limit).toArray();
        }
        else {
            request = applyOptionalLimit(this.db.events.toCollection(), query.limit).toArray();
        }
        const queriedEvents = await request;
        return queriedEvents.map((event) => JSON.parse(event.data));
    }
    async updateEntities(events, transformer) {
        // Attempt to write the events and entities. If some events are duplicates, we'll re-run this function with only the new events.
        await this.db.transaction("readwrite", this.db.entities, this.db.events, this.db.derived_taskComponents, async () => {
            // Fetch the old entities.
            const entityIDs = new Set(events.map(({ entityID }) => entityID));
            const oldEntityRows = (await this.db.entities
                .where(DexieEntityKeys.ID)
                .anyOf([...entityIDs])
                .toArray());
            const entityIDsToRowIDs = new Map(oldEntityRows.map(({ rowID, id }) => [id, rowID]));
            const oldEntityRecordMap = extractEntityRecordMapFromRows(oldEntityRows);
            // Determine which events are new--unfortunately, there's no "cheap" way for us to only insert events which we were missing.
            const oldEvents = await this.getEvents(events.map(({ id }) => id));
            const eventsToAdd = events.filter(({ id }) => !oldEvents.has(id));
            // Compute the new entities.
            const newEntityRecordMap = await transformer(eventsToAdd, oldEntityRecordMap);
            // Write the new entities.
            const newEntityRows = Array();
            for (const [id, record] of newEntityRecordMap) {
                const rowID = entityIDsToRowIDs.get(id);
                newEntityRows.push({
                    ...getEntityRowForEntityRecord(record),
                    // We include the old row ID if we had a record for this row before; otherwise, it's a new entity, and the DB will assign it a row ID.
                    ...(rowID !== undefined && { rowID }),
                });
                await this._onEntityUpdate(oldEntityRecordMap.get(id)?.entity ?? null, record.entity);
            }
            await this.db.entities.bulkPut(newEntityRows);
            await this.db.events.bulkAdd(eventsToAdd.map((event) => ({
                entityID: event.entityID,
                id: event.id,
                data: JSON.stringify(event),
            })));
        });
    }
    async _onEntityUpdate(oldEntity, newEntity) {
        if (newEntity.type !== EntityType.Task) {
            return;
        }
        if (oldEntity) {
            await this.db[DexieTable.DerivedTaskComponents]
                .where(DexieDerivedTaskComponentKeys.TaskID)
                .equals(oldEntity.id)
                .delete();
        }
        if (!newEntity.isDeleted) {
            await this.db[DexieTable.DerivedTaskComponents].bulkPut(Object.entries(newEntity.componentStates).map(([componentID, value]) => ({
                taskID: newEntity.id,
                componentID,
                dueTimestampMillis: value.dueTimestampMillis,
            })));
        }
    }
    async getMetadataValues(keys) {
        const results = await this.db.metadata.bulkGet(keys);
        const output = new Map();
        for (let i = 0; i < keys.length; i++) {
            const value = results[i]?.value;
            if (value) {
                output.set(keys[i], value);
            }
        }
        return output;
    }
    async setMetadataValues(values) {
        await this.db.metadata.bulkPut([...values.entries()].map(([key, value]) => ({ key, value })));
    }
    async _fetchPrimaryKeyFromUniqueKey(table, key, value) {
        // there will never be more than one key
        const afterRowPrimaryKeys = await table
            .where(key)
            .equals(value)
            .primaryKeys();
        return afterRowPrimaryKeys[0];
    }
}
function applyOptionalLimit(query, limit) {
    if (limit) {
        return query.limit(limit);
    }
    else {
        return query;
    }
}
function compareUsingPredicate(clause, predicate) {
    switch (predicate[1]) {
        case "<":
            return clause.below(predicate[2]);
        case "<=":
            return clause.belowOrEqual(predicate[2]);
        case "=":
            return clause.equals(predicate[2]);
        case ">":
            return clause.above(predicate[2]);
        case ">=":
            return clause.aboveOrEqual(predicate[2]);
    }
}
// It's the responsibility of the caller to ensure that the IDs in the listed rows correspond to those of the ID type.
function extractEntityRecordMapFromRows(rows) {
    const output = new Map();
    for (const value of rows) {
        if (!value)
            continue;
        const entity = JSON.parse(value.data);
        output.set(value.id, {
            lastEventID: value.lastEventID,
            lastEventTimestampMillis: value.lastEventTimestampMillis,
            entity,
        });
    }
    return output;
}
function getEntityRowForEntityRecord(record) {
    return {
        id: record.entity.id,
        entityType: record.entity.type,
        lastEventID: record.lastEventID,
        lastEventTimestampMillis: record.lastEventTimestampMillis,
        data: JSON.stringify(record.entity),
    };
}
//# sourceMappingURL=indexedDB.js.map